import React, { useState, useEffect } from 'react';

interface S3ImageProps {
  src?: string;
  alt?: string;
  className?: string;
  [key: string]: any; // Allow other props to pass through
}

// CDN configuration
const CDN_DOMAIN = 'cdn.withvidya.ai';
const S3_BUCKET = 'aicademia';

/**
 * S3Image component that handles S3 URLs by converting them to CDN URLs.
 *
 * Since the S3 bucket is public and served through Cloudflare CDN:
 * - s3://aicademia/vidya-files/... → https://cdn.withvidya.ai/vidya-files/...
 * - https://cdn.withvidya.ai/... → used directly
 * - Regular HTTPS URLs → used directly
 *
 * No API calls needed - all URL conversion happens client-side.
 */
export const S3Image: React.FC<S3ImageProps> = ({ src, alt = '', className = '', ...props }) => {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    // Handle missing src
    if (!src) {
      setError(true);
      return;
    }

    // Reset error state
    setError(false);

    // If URL is already from CDN, use it directly
    if (src.includes(CDN_DOMAIN) ||
        src.includes('.r2.dev') ||
        src.includes('.r2.cloudflarestorage.com') ||
        src.includes('.pages.dev')) {
      setImageUrl(src);
      return;
    }

    // If URL is already presigned (legacy support), use it directly
    if (src.includes('AWSAccessKeyId=') || src.includes('X-Amz-Signature=')) {
      setImageUrl(src);
      return;
    }

    // Convert S3 URL to CDN URL
    if (src.startsWith('s3://')) {
      // s3://aicademia/vidya-files/path/to/file.png
      // → https://cdn.withvidya.ai/vidya-files/path/to/file.png
      const path = src.replace(`s3://${S3_BUCKET}/`, '');
      const cdnUrl = `https://${CDN_DOMAIN}/${path}`;
      setImageUrl(cdnUrl);
      return;
    }

    // Convert HTTPS S3 URLs to CDN URLs
    if (src.startsWith('https://') && src.includes('s3.amazonaws.com')) {
      // https://s3.amazonaws.com/aicademia/vidya-files/... → CDN URL
      // https://aicademia.s3.amazonaws.com/vidya-files/... → CDN URL
      const s3Match = src.match(/https:\/\/s3\.amazonaws\.com\/[^\/]+\/(.+)/) ||
                      src.match(/https:\/\/[^.]+\.s3\.amazonaws\.com\/(.+)/) ||
                      src.match(/https:\/\/[^.]+\.s3\.[^\/]+\.amazonaws\.com\/(.+)/);

      if (s3Match) {
        const path = s3Match[1];
        const cdnUrl = `https://${CDN_DOMAIN}/${path}`;
        setImageUrl(cdnUrl);
        return;
      }
    }

    // For all other URLs (regular HTTPS images), use directly
    setImageUrl(src);
  }, [src]);

  if (error || !imageUrl) {
    return (
      <span className={`inline-block bg-gray-100 rounded-lg px-4 py-4 ${className}`}>
        <span className="text-gray-400 text-sm">Failed to load image</span>
      </span>
    );
  }

  return <img src={imageUrl} alt={alt} className={className} {...props} />;
};
